import { Deployment, foldDeployment_ } from "@/domain/deployment";
import { localesForDeployment, namedLocale, useI18n } from "@/domain/i18n";
import { MenuItem } from "@/domain/menu";
import {
  autoUpdate,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  safePolygon,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import useWindowScrollPosition from "@rehooks/window-scroll-position";
import Link from "next/link";
import { useRouter } from "next/router";
import React from "react";
import LogoCH from "../public/images/gemeinwohl_logo_ch.svg";
import LogoDE from "../public/images/gemeinwohl_logo_de.svg";

interface HeaderProps {
  menuItems: Array<MenuItem>;
  deployment: Deployment;
}

export default function AutoToggleHeader({ menuItems, deployment }: HeaderProps) {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [isSeeThrough, setIsSeeThrough] = React.useState(true);
  const pos = useWindowScrollPosition({ throttle: 100 });

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      setIsSeeThrough(pos.y <= window.innerHeight - 40);
    }
  }, [pos.y, isSeeThrough]);

  return React.useMemo(() => {
    return (
      <header className="landing-page__site-header">
        <div
          ref={headerRef}
          className={`site-header site-header--large ${isSeeThrough ? "site-header--see-through" : ""}`}
          style={isSeeThrough ? {} : { backgroundColor: "white", zIndex: 1000 }}
        >
          <div className="site-header__content">
            <Header menuItems={menuItems} deployment={deployment} />
          </div>
        </div>
      </header>
    );
  }, [isSeeThrough, menuItems, deployment]);
}

export const Header = ({ menuItems, deployment }: HeaderProps) => {
  const I18n = useI18n();
  return (
    <>
      <Link href="/" className="site-header__logo site-logo">
        <h1 className="site-logo__title-fallback">{I18n.t("meta.title")}</h1>
        {foldDeployment_(deployment, {
          ch: () => <LogoCH />,
          de: () => <LogoDE />,
        })}
      </Link>
      <SiteMenu menuItems={menuItems} deployment={deployment} />
    </>
  );
};

interface SiteMenuProps {
  menuItems: Array<MenuItem>;
  deployment: Deployment;
}

function SiteMenu({ menuItems, deployment }: SiteMenuProps) {
  const router = useRouter();

  const [isOpen, setIsOpen] = React.useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: "bottom",
    whileElementsMounted: autoUpdate,
    middleware: [offset(0)],
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, { handleClose: safePolygon() }),
    useFocus(context),
    useDismiss(context),
  ]);

  // Remove locale from path for matching routes, i.e. /de/hello -> /hello (but / -> /)
  const currentPath = router.asPath.match(/^\/(\w\w)\//) ? router.asPath.slice(3) : router.asPath;

  return (
    <>
      <nav className="site-header__nav site-menu">
        <ul>
          {menuItems.map((item, i) => (
            <li
              key={`menu-item-${item.path}`}
              className={[
                i === 0 && "first",
                i === menuItems.length - 1 && "last",
                (item.menu_match && new RegExp(item.menu_match).test(currentPath)) || item.path === currentPath
                  ? "is-active"
                  : false,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <Link href={item.path}>{item.title}</Link>
            </li>
          ))}
        </ul>
      </nav>
      <div className="site-header__language site-menu">
        <ul ref={refs.setReference} id="site-menu-toggle" className="site-menu-toggle" {...getReferenceProps()}>
          <li>
            <a tabIndex={0}>
              {namedLocale(router.locale as string)}{" "}
              <svg width="6" height="10" viewBox="0 0 6 4">
                <path d="M3 4L0 0h6" fill="currentColor" fillRule="evenodd" />
              </svg>
            </a>
          </li>
        </ul>

        {isOpen && (
          <FloatingPortal>
            <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
              <div
                ref={refs.setFloating}
                id="site-menu-dropdown"
                className="site-menu-language-popup"
                style={floatingStyles}
                {...getFloatingProps()}
              >
                <div className="site-menu-language-popup-inner">
                  {localesForDeployment(deployment).map((locale) => (
                    <div key={locale} className={router.locale === locale ? "is-active" : ""}>
                      <Link href={{ pathname: router.pathname, query: router.query }} locale={locale}>
                        {namedLocale(locale)}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </div>
    </>
  );
}
